import { delay, EMPTY, expand, Observable, OperatorFunction, takeLast } from 'rxjs'

const REQUEST_RETRY_COUNT = 10
const REQUEST_RETRY_DELAY = 500

/**
 * Configuration for the retry until updated operator
 */
export interface RetryUntilUpdatedConfig<T> {
  /**
   * Function that checks if the value has been updated by comparing the current response
   * with a previous value. Should return true if updated, false otherwise.
   */
  isUpdated: (response: T, previousValue?: T) => boolean

  /**
   * Function that returns an observable to retry when the value hasn't been updated yet
   */
  retryObservable: () => Observable<T>

  /**
   * Maximum number of retry attempts (default: 10)
   */
  maxRetries?: number

  /**
   * Delay in milliseconds between retries (default: 500)
   */
  retryDelay?: number
}

/**
 * RxJS operator that retries an observable until a value is updated (based on a comparison function).
 * Uses expand to poll until the isUpdated check returns true or max retries is reached.
 *
 * @param config Configuration object with isUpdated check, retry observable, and retry settings
 * @returns An operator function that can be used in a pipe
 *
 * @example
 * ```typescript
 * return this.service.getData(id).pipe(
 *   retryUntilUpdated({
 *     isUpdated: (response, previous) => response.updatedOn !== previous?.updatedOn,
 *     retryObservable: () => this.service.getData(id).pipe(delay(500)),
 *     maxRetries: 10,
 *     retryDelay: 500
 *   }),
 *   takeLast(1)
 * )
 * ```
 */
export function retryUntilUpdated<T>(config: RetryUntilUpdatedConfig<T>): OperatorFunction<T, T> {
  const { isUpdated, retryObservable, maxRetries = REQUEST_RETRY_COUNT, retryDelay = REQUEST_RETRY_DELAY } = config

  return (source: Observable<T>) => {
    let retryCount = 0
    let previousValue: T | undefined

    return source.pipe(
      expand((response) => {
        const updated = isUpdated(response, previousValue)

        if (!updated && retryCount < maxRetries) {
          retryCount++
          previousValue = response
          return retryObservable().pipe(delay(retryDelay))
        } else {
          return EMPTY
        }
      }),
      takeLast(1)
    )
  }
}
